import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Box, Typography } from '@mui/material';
import { gray90, gray100 } from '../../../constants/Colors';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import noImage from '../../assets/noImage.svg';
import { common } from '../../../modules/error';
import { getMaterialNewsList } from '../../../api/db/news';
import {
  ProductGetListType,
  ProductInfosType
} from '../../../validation/products/productType';
import { sentryLog } from '../../../config/sentry';

const styles = {
  row: {
    borderBottom: '1px solid #D8D9DB',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.4px',
    color: gray100
  },
  date: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '22px',
    color: gray90,
    mr: '16px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      rowGap: '8px'
    },
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: '16px 0',
    textDecoration: 'none'
  },
  imageTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  image: {
    width: '40px',
    height: '40px',
    mr: '16px'
  }
};

const MaterialNewsItem = (news: ProductInfosType): React.ReactElement => {
  const isMobile: boolean = useMediaQuery({ query: '(max-width: 960px)' });
  const mainImage =
    news.images != null
      ? news.images.find((image) => image.sort === 1)?.filepath
      : noImage;
  const showText = () => {
    let text = `【${news.name}】${news.summary}`;
    if (isMobile && text.length > 40) {
      text = text.slice(0, 40) + '...';
    }
    return text;
  };
  return (
    <Box sx={styles.row}>
      <Box
        sx={styles.body}
        component={'a'}
        href={'/material/' + news.productId}
        target="_blank"
      >
        <Typography sx={styles.date}>{news.firstApprovedAt}</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <Box sx={styles.imageTitleContainer}>
            <Box component="img" src={mainImage ?? noImage} sx={styles.image} />
            <Typography color={gray100}>{showText()}</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: gray100 }} />
        </Box>
      </Box>
    </Box>
  );
};

interface Props {
  currentPage: number;
  setErrorText: (value: string) => void;
  setLoading: (value: boolean) => void;
  setOpen: (value: boolean) => void;
  setTotalPages: (value: number) => void;
}

export const MaterialNewsItemList = (props: Props): React.ReactElement => {
  const { currentPage, setErrorText, setLoading, setOpen, setTotalPages } =
    props;
  const [newsList, setNewsList] = useState<ProductInfosType[]>([]);
  const dataCountLimit = 10;

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const fetchedData = (
          await getMaterialNewsList(
            dataCountLimit,
            (currentPage - 1) * dataCountLimit
          )
        ).data as ProductGetListType;

        const totalPages = Math.ceil(fetchedData.total_count / 10);
        setTotalPages(totalPages);

        const productsList = fetchedData.products.map((product) => ({
          productId: product.id,
          productInfoId: product.product_info_id,
          firstApprovedAt: product.first_approved_at,
          status: 1,
          alias: product.alias,
          category: product.category.name + ' / ' + product.material.name,
          name: product.name,
          specification: product.specification,
          summary: product.summary,
          emissionIntensity: product.emission_intensity,
          reduceRate: product.reduce_rate,
          calculationValue: product.emission_intensity,
          calculationBasisType: [product.calculation_basis_type],
          calculationBasisName: product.calculation_basis_name,
          company: product.users.company,
          myListNum: product.mylist_number,
          score: product.evaluation_score,
          recommendFlg: [product.recommend_flg],
          title: product.evaluations.title,
          content: product.evaluations.content,
          createdDate: product.created_at,
          updateDate: product.updated_at,
          myListFlg: Boolean(product.mylist_flg),
          favoriteFlg: Boolean(product.favorite_flg),
          referenceUnitPrice: product.reference_unit_price,
          reductionAmount: product.calculated_emission_intensity,
          price: product.reference_unit_price,
          images: product.image,
          co2Unit: product.material.co2_unit,
          costUnit: product.material.cost_unit
        }));

        const displayData = productsList.map((item: ProductInfosType) => {
          // 日付の表示形式を変更
          const publishedDate = new Date(item.firstApprovedAt ?? '');
          const formattedPublishedDate = publishedDate
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, '/');
          return {
            ...item,
            firstApprovedAt: formattedPublishedDate
          };
        });
        setNewsList(displayData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        setErrorText(common);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentPage]);

  return (
    <>
      {newsList.map((news) => (
        <MaterialNewsItem {...news} key={news.productId} />
      ))}
    </>
  );
};
